import React from 'react';
import { cn } from '../../lib/utils';
import { useDropdownContext } from './Dropdown';

interface DropdownItemProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  rightElement?: React.ReactNode;
  disabled?: boolean;
  active?: boolean;
  className?: string;
  closeOnClick?: boolean;
  description?: string;
  variant?: 'default' | 'danger';
}

export function DropdownItem({
  children,
  onClick,
  leftIcon,
  rightIcon,
  rightElement,
  disabled = false,
  active = false,
  className = '',
  closeOnClick,
  description,
  variant = 'default',
}: DropdownItemProps) {
  const { isMobile, closeOnItemClick, handleClose } = useDropdownContext();

  // Determine whether to close on click
  const shouldCloseOnClick = closeOnClick !== undefined ? closeOnClick : closeOnItemClick;

  // Handle click with optional closing
  const handleClick = (e: React.MouseEvent) => {
    if (disabled) return;

    // Call the provided onClick handler
    onClick?.(e);

    // Close the dropdown if needed
    if (shouldCloseOnClick) {
      handleClose();
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'flex items-center w-full',
        isMobile
          ? 'py-3 px-4 text-lg' // Standardized padding for mobile touch targets
          : 'py-2.5 px-3', // Standardized padding for desktop
        active
          ? 'bg-blue-50 text-blue-700 font-medium'
          : variant === 'danger'
            ? 'text-red-600 hover:bg-red-50 hover:font-medium'
            : 'text-gray-700 hover:bg-[var(--content-bg-color)] hover:font-medium',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        className
      )}
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
    >
      {/* Left icon */}
      {leftIcon && (
        <span
          className={cn(
            'flex-shrink-0 flex items-center justify-center',
            variant === 'danger' ? 'text-red-500' : 'text-blue-500',
            children ? 'mr-3' : ''
          )}
        >
          {leftIcon}
        </span>
      )}

      {/* Main content */}
      <div className="flex-grow min-w-0">
        {/* Main text */}
        <div className="flex items-center">
          <span className="truncate">{children}</span>
        </div>

        {/* Optional description */}
        {description && <div className="text-xs text-gray-500 mt-0.5 truncate">{description}</div>}
      </div>

      {/* Right element (badge, etc.) */}
      {rightElement && <span className="flex-shrink-0 ml-2">{rightElement}</span>}

      {/* Right icon */}
      {rightIcon && <span className="flex-shrink-0 ml-2">{rightIcon}</span>}
    </div>
  );
}
